import React, { createContext, useState, useContext, useMemo, useCallback, useEffect } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, onIdTokenChanged, signOut } from 'firebase/auth';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import PropTypes from 'prop-types';

const AuthContext = createContext(null);

/* eslint-disable no-undef */
// Firebase configuration, baked in at build time.
//
// Each deployment region authenticates against its own Firebase project, so these come from
// REACT_APP_FIREBASE_* rather than being hardcoded. The defaults are the US project's values,
// so a build with none of them set produces exactly the bundle it always did.
//
// These are public by design — a Firebase web apiKey identifies the project, it does not
// authorise anything. Access is governed by Security Rules and the API key's referrer
// restrictions, not by keeping this string secret.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyBKPjoc--PyDDZqUneivOprd3cxS3yhQ7Q",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "astrostockcharts-site-prod.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "astrostockcharts-site-prod",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "astrostockcharts-site-prod.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "8836470984",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:8836470984:web:86c0b4a70349ed532fadfd",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-MM2PV6ERX3"
};
/* eslint-enable no-undef */

/**
 * How long startup may wait for Firebase to report an auth state before giving up.
 *
 * Generous enough that a slow network is not mistaken for a failure, short enough that a real
 * failure does not look like a hung application.
 */
const AUTH_READY_TIMEOUT_MS = 10000;

export const AuthProvider = ({ children }) => {
    console.log('🔥 AuthProvider is rendering...');
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    const [auth, setAuth] = useState(null);
    const [loading, setLoading] = useState(true); // Add loading state
    const [authInitFailed, setAuthInitFailed] = useState(false);
    const [clockOffset, setClockOffset] = useState(0); // Add clock offset state

    useEffect(() => {
        console.log('🚀 Firebase initialization useEffect running...');
        // Wrapped because a throw in here is unrecoverable and used to be silent: `auth` would stay
        // null, the effect below that clears `loading` early-returns on a null auth, and the app
        // would sit on ProtectedRoute's "Loading..." for ever with nothing said about why.
        try {
            // Ensure Firebase is initialized only once
            if (!getApps().length) {
                console.log('📱 Initializing Firebase app...');
                const app = initializeApp(firebaseConfig);
                const authInstance = getAuth(app);
                console.log('🔐 Firebase auth instance created:', authInstance);
                setAuth(authInstance);
            } else {
                console.log('📱 Firebase app already exists, getting auth...');
                const authInstance = getAuth();
                console.log('🔐 Firebase auth instance retrieved:', authInstance);
                setAuth(authInstance);
            }
        } catch (err) {
            console.error('❌ Firebase auth could not be initialised. Signing in is unavailable.', err);
            // Stop blocking the app. Unauthenticated is the honest state when there is no auth to
            // ask, and it lands the user on the login page instead of a permanent spinner.
            setAuthInitFailed(true);
            setLoading(false);
        }
    }, []);

    /**
     * Watchdog for a startup that never resolves.
     *
     * `loading` is cleared by the onIdTokenChanged callback, which normally fires within moments of
     * subscribing. If it never fires — Firebase unreachable, a blocked request, an auth instance
     * that failed in a way that did not throw — nothing else clears the flag, and ProtectedRoute
     * renders "Loading..." indefinitely. There is no recovery from that and no error to read.
     *
     * The timeout bounds it: the app proceeds as unauthenticated, which sends the user to the login
     * page rather than a dead screen, and logs why. It is a floor under the failure, not a fix for
     * whatever caused it — if this fires, the console message is the thing to act on.
     */
    useEffect(() => {
        if (!loading) return undefined;
        const timer = setTimeout(() => {
            console.error(
                `⏱️ Auth did not resolve within ${AUTH_READY_TIMEOUT_MS}ms. Continuing as signed out. ` +
                'Firebase never reported an auth state — check the network tab for requests to ' +
                "identitytoolkit.googleapis.com, and the HTTP referrer allowlist on the browser key."
            );
            setLoading(false);
        }, AUTH_READY_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [loading]);

    useEffect(() => {
        const syncTime = async (retryCount = 0) => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/time`, { 
                    timeout: 5000, // Reduced timeout to fail faster
                    validateStatus: (status) => status < 500 // Don't throw for 4xx errors
                });
                const serverTime = response.data.serverTime;
                const clientTime = Date.now();
                setClockOffset(serverTime - clientTime);
            } catch (error) {
                if (error.code === 'ECONNABORTED') {
                    console.warn('⏰ Time sync timeout - backend may be slow or unresponsive');
                } else if (error.response) {
                    console.warn('⚠️ Time sync failed with server error:', error.response.status);
                } else {
                    console.warn('⚠️ Could not sync time with server:', error.message);
                }
                
                // Retry once after 2 seconds if this was the first attempt
                if (retryCount === 0) {
                    console.log('🔄 Retrying time sync in 2 seconds...');
                    setTimeout(() => syncTime(1), 2000);
                } else {
                    console.log('❌ Time sync failed after retry, continuing with clock offset 0');
                    setClockOffset(0);
                }
            }
        };

        syncTime();
    }, []);

        useEffect(() => {
        if (!auth) {
            return;
        }

        // Set up a timer to proactively refresh the token
        const tokenRefreshInterval = setInterval(async () => {
            const user = auth.currentUser;
            if (user) {
                try {
                    const tokenResult = await user.getIdTokenResult();
                    const expirationTime = new Date(tokenResult.expirationTime).getTime();
                    const now = Date.now() + clockOffset; // Apply offset to get server-relative time

                    // Refresh if the token is expiring in the next 5 minutes
                    if (expirationTime - now < 5 * 60 * 1000) {
                        await user.getIdToken(true); // Force refresh
                    }
                } catch (error) {
                    console.error('Error during scheduled token refresh:', error);
                }
            }
        }, 60 * 1000); // Check every 60 seconds

        const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
            console.log('🔥 onIdTokenChanged fired, firebaseUser:', firebaseUser);
            if (firebaseUser) {
                const idToken = await firebaseUser.getIdToken();
                console.log('🎫 Got ID token, setting in localStorage and state');
                localStorage.setItem('authToken', idToken);
                setToken(idToken);

                try {
                    console.log('👤 Fetching user profile from /api/user/me...');
                    // Use a temporary, local axios instance with the new token to avoid race conditions
                    const tempApi = axios.create({
                        baseURL: API_BASE_URL,
                        headers: { 'Authorization': `Bearer ${idToken}` },
                        timeout: 180000, // 3 minutes
                    });
                    
                    // First, sync the user to ensure they exist in Firestore
                    console.log('🔄 Syncing user to backend database...');
                    try {
                        await tempApi.post('/api/auth/sync');
                        console.log('✅ User sync completed');
                    } catch (syncError) {
                        console.warn('⚠️ User sync failed, but continuing with authentication:', syncError.response?.status, syncError.response?.data);
                        // Don't logout on sync failure - user might already exist or sync might be optional
                    }
                    
                    const response = await tempApi.get('/api/user/me');
                    console.log('✅ User profile fetched:', response.data);
                    setUser(response.data);
                } catch (error) {
                    console.error('❌ Failed to fetch user profile, logging out:', error);
                    console.error('Error details:', error.response?.status, error.response?.data);
                    // Only logout if it's a critical authentication error (401 Unauthorized)
                    if (error.response?.status === 401) {
                        await signOut(auth);
                    } else {
                        // For other errors (network, server errors), keep user logged in
                        // Set a minimal user object to maintain authentication state
                        console.warn('⚠️ Non-critical error during user profile fetch, maintaining authentication');
                        setUser({ uid: firebaseUser.uid, email: firebaseUser.email, fallback: true });
                    }
                }
            } else {
                console.log('🔴 Firebase user is null, clearing state');
                setUser(null);
                setToken(null);
                localStorage.removeItem('authToken');
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            clearInterval(tokenRefreshInterval);
        };
    }, [auth, clockOffset]);

    const login = useCallback(async (email, password) => {
        console.log(' Login called with:', email);
        if (loading || !auth) {
            console.error("Login attempted while auth is still loading or not initialized.");
            throw new Error("Authentication not ready");
        }
        try {
            console.log(' Attempting Firebase sign in...');
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            await userCredential.user.getIdToken(); // Get token to trigger onIdTokenChanged
            console.log(' Firebase login successful');

            // The onIdTokenChanged listener will handle setting the user state.
            return true;
        } catch (error) {
            console.error(' Firebase login error:', error);
            throw error;
        }
    }, [auth, loading]);

    const logout = useCallback(async () => {
        if (!auth) return; // Guard against calling logout before auth is initialized
        try {
            await signOut(auth);
            // The onAuthStateChanged listener will handle clearing state
        } catch (error) {
            console.error("Logout error:", error);
        }
    }, [auth]);

    const api = useMemo(() => {
        const instance = axios.create({
            baseURL: API_BASE_URL,
            timeout: 180000, // 3 minutes
        });

        instance.interceptors.request.use(
            (config) => {
                if (token) {
                    config.headers['Authorization'] = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        instance.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response && error.response.status === 401) {
                    console.error('Unauthorized (401): Logging out.');
                    logout();
                }
                return Promise.reject(error);
            }
        );

        return instance;
    }, [token, logout]);

    // Case-insensitive: live Firestore documents were written by several code paths over
    // time, and the backend compares these the same way (User.isAdmin/isTrial). A strict
    // === 'ADMIN' would silently drop admin privileges for a document holding 'admin'.
    const subscriptionType = user?.subscriptionType?.toUpperCase();
    const isTrial = subscriptionType === 'TRIAL';
    const isAdmin = subscriptionType === 'ADMIN';
    const isExpired = user?.subscriptionEndDate
        ? new Date(user.subscriptionEndDate) < new Date()
        : false;

    const authContextValue = useMemo(() => ({
        isAuthenticated: !!user,
        token,
        user,
        login,
        logout,
        loading,
        authInitFailed,
        api,
        isTrial,
        isAdmin,
        isExpired
    }), [token, user, login, logout, loading, authInitFailed, api, isTrial, isAdmin, isExpired]);

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAuth = () => {
    return useContext(AuthContext);
};